/**
 * Certification Body - My Accreditation
 * View own accreditation status and apply for accreditation
 */
import React, { useState } from 'react';
import { useCBAccreditations, useApplyCBAccreditation, useAccreditationScopes } from '../hooks/useAccreditation';
import {
  AccreditationCard,
  AccreditationStatusBadge,
  AccreditationTimeline,
  AccreditationScopeSelector,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage
} from '../components/AccreditationComponents';
import { formatDate } from '../api/accreditationService';

const MyCBAccreditation = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { accreditations, loading, error, refetch } = useCBAccreditations();
  const { scopes, loading: scopesLoading } = useAccreditationScopes(true);
  const { apply, loading: applyLoading, error: applyError, success } = useApplyCBAccreditation();

  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [evidenceNotes, setEvidenceNotes] = useState('');

  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    if (selectedScopes.length === 0) {
      alert('Please select at least one accreditation scope');
      return;
    }

    try {
      await apply({
        company_id: user.company_id,
        scope_ids: selectedScopes,
        evidence_refs: {
          notes: evidenceNotes
        }
      });
      setShowApplicationForm(false);
      setSelectedScopes([]);
      setEvidenceNotes('');
      refetch();
    } catch (err) {
      // Error handled by hook
    }
  };

  const containerStyle = {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerStyle = {
    marginBottom: '32px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '8px'
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#fff',
    background: '#27ae60',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const formStyle = {
    background: '#fff',
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    marginBottom: '24px'
  };

  const fieldStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333'
  };

  const textareaStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    minHeight: '120px',
    fontFamily: 'inherit'
  };

  const sectionStyle = {
    background: '#fff',
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    marginBottom: '24px'
  };

  const sectionTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '20px'
  };

  const infoBoxStyle = (type) => ({
    padding: '16px',
    background: type === 'info' ? '#e3f2fd' : '#fff3e0',
    border: `1px solid ${type === 'info' ? '#90caf9' : '#ffb74d'}`,
    borderRadius: '8px',
    marginBottom: '24px',
    color: '#333'
  });

  if (loading || scopesLoading) return <LoadingSpinner />;

  const activeAccreditation = accreditations.find(a => a.status === 'granted');
  const renewingAccreditation = accreditations.find(a => a.status === 'renewing');
  const pendingAccreditation = accreditations.find(a => ['applied', 'under_review', 'document_review', 'initial_assessment', 'witness_audit', 'technical_review'].includes(a.status));
  const canApply = !activeAccreditation && !renewingAccreditation && !pendingAccreditation;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>My Accreditation</h1>
        <p style={{ color: '#666', fontSize: '1rem' }}>
          Manage your ICSC Certification Body accreditation
        </p>
      </div>

      <ErrorMessage message={error || applyError} />
      {success && <SuccessMessage message="Application submitted successfully! ICSC will review your application." />}

      {activeAccreditation ? (
        <>
          <div style={infoBoxStyle('info')}>
            <strong>Your certification body is accredited</strong>
            <div style={{ marginTop: '8px' }}>
              Accreditation Number: <strong>{activeAccreditation.accreditation_number}</strong><br/>
              Expiry Date: <strong>{formatDate(activeAccreditation.expiry_date)}</strong>
              {activeAccreditation.next_surveillance_due && (
                <><br/>Next Surveillance: <strong>{formatDate(activeAccreditation.next_surveillance_due)}</strong></>
              )}
            </div>
          </div>

          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Accreditation Details</h2>
            <div style={{ marginBottom: '16px' }}>
              <strong>Status:</strong> <AccreditationStatusBadge status={activeAccreditation.status} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong>Accredited Scopes:</strong>
              <div style={{ marginTop: '8px' }}>
                {activeAccreditation.scopes && activeAccreditation.scopes.map((scope, index) => (
                  <span key={index} style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    background: '#e8f5e9',
                    color: '#2e7d32',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    marginRight: '8px',
                    marginBottom: '8px'
                  }}>
                    {scope.scope?.scope_code} - {scope.scope?.scope_name}
                  </span>
                ))}
              </div>
            </div>

            <AccreditationTimeline accreditation={activeAccreditation} />
          </div>
        </>
      ) : renewingAccreditation ? (
        <>
          <div style={infoBoxStyle('warning')}>
            <strong>Renewing Accreditation</strong>
            <div style={{ marginTop: '8px' }}>
              Your accreditation is being renewed.
              Status: <strong style={{ textTransform: 'capitalize' }}>{renewingAccreditation.status.replace('_', ' ')}</strong>
            </div>
          </div>

          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Current Accreditation</h2>
            <div style={{ marginBottom: '16px' }}>
              <strong>Accreditation Number:</strong> {renewingAccreditation.accreditation_number}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Current Expiry Date:</strong> {formatDate(renewingAccreditation.expiry_date)}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Renewal Submission Date:</strong> {formatDate(renewingAccreditation.application_date)}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Status:</strong> <AccreditationStatusBadge status={renewingAccreditation.status} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong>Accredited Scopes:</strong>
              <div style={{ marginTop: '8px' }}>
                {renewingAccreditation.scopes && renewingAccreditation.scopes.map((scope, index) => (
                  <span key={index} style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    background: '#e8f5e9',
                    color: '#2e7d32',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    marginRight: '8px',
                    marginBottom: '8px'
                  }}>
                    {scope.scope?.scope_code} - {scope.scope?.scope_name}
                  </span>
                ))}
              </div>
            </div>

            <AccreditationTimeline accreditation={renewingAccreditation} />
          </div>
        </>
      ) : pendingAccreditation ? (
        <>
          <div style={infoBoxStyle('warning')}>
            <strong> Application under review</strong>
            <div style={{ marginTop: '8px' }}>
     Your accreditation application is being reviewed by ICSC.
              Status: <strong style={{ textTransform: 'capitalize' }}>{pendingAccreditation.status.replace('_', ' ')}</strong>
            </div>
          </div>

          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Application Status</h2>
            <div style={{ marginBottom: '16px' }}>
              <strong>Application Number:</strong> {pendingAccreditation.accreditation_number}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Applied:</strong> {formatDate(pendingAccreditation.application_date)}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Status:</strong> <AccreditationStatusBadge status={pendingAccreditation.status} />
            </div>

            <AccreditationTimeline accreditation={pendingAccreditation} />
          </div>
        </>
      ) : (
        <>
          {!showApplicationForm ? (
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>Apply for Accreditation</h2>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Apply to become an ICSC accredited certification body. This will enable you to issue ISO certificates to your clients under ICSC accreditation.
              </p>

              <div style={infoBoxStyle('info')}>
                <strong>Requirements:</strong>
                <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                  <li>Active ICSC membership (£500/year)</li>
                  <li>Initial assessment fee (£400)</li>
                  <li>Qualified auditors for each ISO scope</li>
                  <li>Quality management system in place</li>
                  <li>Witness audit compliance</li>
                </ul>
              </div>

              <button style={buttonStyle} onClick={() => setShowApplicationForm(true)}>
                Start Application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmitApplication} style={formStyle}>
              <h2 style={sectionTitleStyle}>Accreditation Application</h2>

              <div style={fieldStyle}>
                <label style={labelStyle}>Select ISO Scopes *</label>
                <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: '12px' }}>
                  Choose which ISO standards you want to be accredited for:
                </p>
                {scopes && scopes.length > 0 ? (
                  <AccreditationScopeSelector
                    scopes={scopes}
                    selectedScopes={selectedScopes}
                    onChange={setSelectedScopes}
                  />
                ) : (
                  <div style={{
                    padding: '16px',
                    background: '#fff3e0',
                    border: '1px solid #ffb74d',
                    borderRadius: '4px',
                    color: '#666'
                  }}>
                    <strong>No accreditation scopes available</strong>
                    <p>Please contact ICSC support to confirm available accreditation scopes.</p>
                  </div>
                )}
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Supporting Information</label>
                <textarea
                  value={evidenceNotes}
                  onChange={(e) => setEvidenceNotes(e.target.value)}
                  style={textareaStyle}
                  placeholder="Provide any additional information, evidence, or notes to support your application..."
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  style={buttonStyle}
                  disabled={applyLoading || selectedScopes.length === 0}
                >
                  {applyLoading ? 'Submitting...' : 'Submit Application'}
                </button>
                <button
                  type="button"
                  style={{ ...buttonStyle, background: '#95a5a6' }}
                  onClick={() => setShowApplicationForm(false)}
                  disabled={applyLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {accreditations.length > 0 && (
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Application History</h2>
          {accreditations.map((acc) => (
            <AccreditationCard key={acc.id} accreditation={acc} type="cb" />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCBAccreditation;
